import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled, { css } from 'styled-components';
import { Context } from '@components/Context';
import { CurrencyObj } from '@contracts';
import { getPriceText } from '@utils/price';
import Colors from './Colors';

interface PriceDisplayProps {
  currency?: CurrencyObj;
  price: string | number;
  includePlusSign?: boolean;
  isStriked?: boolean;
  priceSuffix?: string;
  color?: string;
  isFaded?: boolean;
  variant?: string;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  currency,
  price,
  includePlusSign = false,
  isStriked,
  priceSuffix,
  color,
  isFaded,
  variant = 'default'
}) => {
  const {
    state: { country, currency: currencyFallback }
  } = useContext(Context);
  const { t } = useTranslation();

  const formattedPrice = getPriceText(Number(price), country, currency || currencyFallback, t, false, false);
  const fullPriceDisplay = (includePlusSign ? '+' : '') + formattedPrice.fullString + (priceSuffix || '');

  return (
    <StyledPrice
      $variant={variant}
      $isStriked={isStriked}
      $isFaded={isFaded}
      $color={color}
      data-testid="price-display"
      data-value={fullPriceDisplay}
    >
      {includePlusSign && '+'}
      {formattedPrice.prefix}
      {formattedPrice.main}
      {formattedPrice.sup && <sup>{formattedPrice.sup}</sup>}
      {formattedPrice.suffix}
      {priceSuffix}
    </StyledPrice>
  );
};

export default PriceDisplay;

const StyledPrice = styled.span<{
  $variant: 'default' | 'crossSell';
  $customStyles?: any;
  $isStriked?: boolean;
  $isFaded?: boolean;
  $color?: string;
}>`
  font-family: 'Domaine';
  font-weight: 600;
  font-size: 18px;
  line-height: 110%;
  display: flex;
  align-items: center;
  text-align: center;
  color: ${({ $color }) => $color || 'inherit'};
  white-space: nowrap;

  sup {
    top: -2px;
    margin: 0 2px;
    font-size: 75%;
  }

  ${({ $variant }) =>
    $variant === 'crossSell' &&
    `
      font-size: 14px;
      font-family: 'BrownStd';
  `}

  ${({ $isStriked }) =>
    $isStriked &&
    css`
      position: relative;

      &:before {
        content: '';
        position: absolute;
        top: 50%;
        margin-top: -1px;
        height: 2px;
        width: 100%;
        background: ${Colors.navyBlue};
        z-index: 2;
      }
    `}

  ${({ $isFaded }) =>
    $isFaded &&
    css`
      opacity: 0.5;
    `}
`;
